import type { NoticeStore } from "shared/react";
import type { APIProps, APIReturn } from "shared/util/api";
import type { Callback, Resource } from "shelving";
import { Feedback, call, writeChanges } from "shelving";
import { callFunction } from "shared/firebase/functions";
import { NOTICES } from "shared/react";
import { MEMORY } from "./db.js";

/**
 * Call the backend API.
 * - Applies any returned changes to the cache.
 * - Shows an error notice if something went wrong.
 */
export async function callAPI<P, R>(resource: Resource<P, R>, unsafePayload: P): Promise<R> {
	try {
		const { value: unsafeValue, changes, feedback } = await callFunction<APIProps<unknown>, APIReturn<unknown>>("api", { action: resource.name, payload: resource.prepare(unsafePayload) });
		const value = resource.validate(unsafeValue);
		if (changes) writeChanges(MEMORY, ...changes);
		if (feedback) NOTICES.success(feedback);
		return value;
	} catch (thrown) {
		NOTICES.error(thrown instanceof Feedback ? thrown : "Unknown error");
		throw thrown;
	}
}

/** Get a function that calls the backend API. */
export function getCallAPI<P, R>(resource: Resource<P, R>, unsafePayload: P): Callback {
	return () => call(callAPI, resource, unsafePayload);
}

/**
 * Call the backend API and show a notice throughout the process.
 * - Shows a `loading` notice that transforms to a `success` notice when loading is finished.
 * - Applies any returned changes to the cache.
 * - Shows an error notice if something went wrong.
 */
export async function callAPIWithNotice<P, R>(resource: Resource<P, R>, unsafePayload: P, notice: NoticeStore = NOTICES.load()): Promise<R> {
	notice.load();
	try {
		const { value: unsafeValue, changes, feedback } = await callFunction<APIProps<unknown>, APIReturn<unknown>>("api", { action: resource.name, payload: resource.prepare(unsafePayload) });
		const value = resource.validate(unsafeValue);
		if (changes) writeChanges(MEMORY, ...changes);
		if (feedback) notice.success(feedback);
		else notice.close();
		return value;
	} catch (thrown) {
		notice.error(thrown instanceof Feedback ? thrown : "Unknown error");
		throw thrown;
	}
}

/** Get a function that calls the backend API and shows a notice throughout the process. */
export function getCallAPIWithNotice<P, R>(resource: Resource<P, R>, unsafePayload: P): Callback {
	return () => call(callAPIWithNotice, resource, unsafePayload);
}
